import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Paper, Typography, Stack, Chip,
    IconButton, Table, TableHead, TableBody, TableRow, TableCell, Tooltip,
} from "@mui/material";
import RefreshRoundedIcon            from "@mui/icons-material/RefreshRounded";
import CancelScheduleSendRoundedIcon from "@mui/icons-material/CancelScheduleSendRounded";
import { mailApi } from "../../api/mailApi";
import { notify }  from "../../../../util/toast";
import { confirm } from "../../../../util/confirmDialog";

const STATUS_META = {
    SCHEDULED: { label: "예약됨",   color: "#1d4ed8", bg: "#dbeafe" },
    SENDING:   { label: "발송중",   color: "#b45309", bg: "#fef3c7" },
    SENT:      { label: "발송완료", color: "#15803d", bg: "#dcfce7" },
    FAILED:    { label: "실패",     color: "#b91c1c", bg: "#fee2e2" },
    DRAFT:     { label: "취소됨",   color: "#64748b", bg: "#f1f5f9" },
};

function StatusChip({ status }) {
    const m = STATUS_META[status] || STATUS_META.SCHEDULED;
    return <Chip label={m.label} size="small"
                 sx={{ height: 20, fontSize: 11, fontWeight: 700, color: m.color, bgcolor: m.bg }} />;
}

const fmt = (v) => (v ? String(v).slice(5, 16).replace("T", " ") : "-");

/**
 * 예약 발송 이력 — 예약함의 '예약 메일' 탭.
 *
 * <p>발송이 끝나면 메일이 보낸함(SENT)으로 이동해 예약함 폴더 목록에선 사라진다. 그래서 여기서는
 * 폴더가 아니라 "내가 예약한 메일(scheduled_at 있음)" 을 상태와 함께 조회해, 예약대기/발송완료/실패를
 * 계속 보여준다 — 예약 발송이 성공했는지 사용자가 확인할 수 있게.
 */
export default function ScheduledMailPanel({ onOpenMail }) {
    const [rows, setRows] = useState([]);

    const load = useCallback(async () => {
        try {
            const res = await mailApi.listScheduledSends(0, 100);
            const data = res?.data ?? res ?? {};
            setRows(Array.isArray(data) ? data : (data.content || []));
        } catch { /* noop */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    // 예약대기/발송중이 남아있으면 10초마다 폴링 — 발송완료/실패로 바뀌는지 추적.
    const anyPending = rows.some(r => r.status === "SCHEDULED" || r.status === "SENDING");
    useEffect(() => {
        if (!anyPending) return;
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
    }, [anyPending, load]);

    const handleCancel = async (id) => {
        const ok = await confirm({
            title: "예약 취소",
            message: "이 예약 메일을 취소하고 임시보관함으로 옮길까요?",
            confirmLabel: "예약 취소",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await mailApi.cancelScheduled(id);
            notify("예약을 취소했습니다. 임시보관함에서 확인하세요.", "success");
            load();
        } catch (e) { notify(e?.message || "예약 취소에 실패했습니다.", "error"); }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 13, color: "#64748b", flex: 1 }}>
                    예약 발송한 메일의 상태입니다. 발송 완료 후에도 성공 여부를 계속 확인할 수 있어요. (작성은 <b>메일 쓰기 → 예약</b>)
                </Typography>
                <IconButton size="small" onClick={load}><RefreshRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
            </Stack>

            <Paper variant="outlined" sx={{ borderColor: "#e2e8f0", borderRadius: 2, overflow: "hidden" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ "& th": { fontSize: 12, fontWeight: 700, color: "#64748b", bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" } }}>
                            <TableCell>제목</TableCell>
                            <TableCell align="center" sx={{ width: 90 }}>상태</TableCell>
                            <TableCell align="right" sx={{ width: 150 }}>예약 시각</TableCell>
                            <TableCell align="right" sx={{ width: 150 }}>발송 시각</TableCell>
                            <TableCell align="right" sx={{ width: 56 }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                                    예약 발송 내역이 없습니다. <b>메일 쓰기 → 예약</b>으로 예약 메일을 보내보세요.
                                </TableCell>
                            </TableRow>
                        ) : rows.map((r) => (
                            <TableRow key={r.id} hover
                                      sx={{ cursor: onOpenMail ? "pointer" : "default" }}
                                      onClick={() => onOpenMail?.(r.id)}>
                                <TableCell sx={{ maxWidth: 340 }}>
                                    <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                        {r.subject || "(제목 없음)"}
                                    </Typography>
                                    {r.status === "FAILED" && r.failureReason && (
                                        <Typography noWrap title={r.failureReason} sx={{ fontSize: 11, color: "#b91c1c", mt: 0.2 }}>
                                            {r.failureReason}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="center"><StatusChip status={r.status} /></TableCell>
                                <TableCell align="right" sx={{ fontSize: 12, color: "#334155", whiteSpace: "nowrap" }}>{fmt(r.scheduledAt)}</TableCell>
                                <TableCell align="right" sx={{ fontSize: 12, color: r.sentAt ? "#15803d" : "#94a3b8", whiteSpace: "nowrap" }}>{fmt(r.sentAt)}</TableCell>
                                <TableCell align="right">
                                    {r.status === "SCHEDULED" && (
                                        <Tooltip title="예약 취소">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCancel(r.id); }}>
                                                <CancelScheduleSendRoundedIcon sx={{ fontSize: 18, color: "#b91c1c" }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}
